import { FormControl, InputLabel, MenuItem, Select, Stack, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, tableCellClasses, Paper, Typography } from "@mui/material";
import Button, { ButtonProps } from '@mui/material/Button';
import { purple } from '@mui/material/colors';
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ResultsService } from "../../../services/results/ResultsService";
import { AppContext } from "../../../contexts/AppContext";
import { StatusCodes } from "http-status-codes";
import { toast } from "react-toastify";
import DeleteIcon from '@mui/icons-material/Delete';
import { setStyle } from "../../../util";



interface Player {
  id: string,
  user: string;
  date: Date;
  score: number;
  server?: string;  // present when admin requests ALL or any server
}


const Scoreboard = () => {
  const navigate = useNavigate();

  const goToHome = () => {
    window.location.reload();
  }

  const { jwtToken, server, role, defaultValues } = useContext(AppContext);
  const [players, setPlayers] = useState<Player[]>([]);
  const [rowColor, setRowColor] = useState<string>("black");
  // Admins can filter the leaderboard by server; default ALL to show every result.
  const [filterServer, setFilterServer] = useState<string>(role === "ADMIN" ? "ALL" : (server || ""));

  useEffect(() => {
    async function fetchData() {
      if (jwtToken && filterServer) {
        const res = await ResultsService.getLeaderboard(jwtToken, filterServer);
        console.log(res.data)
        setPlayers(res.data);
      }
    }

    fetchData();

    if (defaultValues) { setStyle(defaultValues); setRowColor(defaultValues["theme"]["color_two"]) }
  }, [jwtToken, filterServer])

  function printDate(date: Date) {
    const dateStr = date.toString();
    const utcDate = new Date(dateStr);
    return utcDate.toLocaleString();
  }

  function deletePlayer(id: string, rowServer?: string) {
    // Prefer the row's own server (set when admin views combined or any server).
    // Falls back to the viewer's server for legacy single-server views.
    const targetServer = rowServer || server;
    console.log(id, "server=", targetServer)
    if (jwtToken && targetServer) {
      ResultsService.deleteResult(jwtToken, targetServer, id).then((res) => {
        if (res.status === StatusCodes.OK) {
          const playersClone = players.slice();
          for (let i = 0; i < playersClone.length; ++i) {
            if (playersClone[i].id === id) {
              playersClone.splice(i, 1);
              break;
            }
          }
          setPlayers(playersClone);
          toast("Enregistrement supprimé.");
        } else {
          toast("Suppression échouée.");
        }
      });
    }

  }

  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      color: rowColor
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));
  
  const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(odd)': {
      backgroundColor: "lightgrey",
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  return (
    <Paper sx={{ maxHeight: "70vh", p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ m: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Palmarès
        </Typography>
        {role === "ADMIN" && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Serveur</InputLabel>
            <Select
              value={filterServer}
              label="Serveur"
              onChange={(e) => setFilterServer(e.target.value)}
            >
              <MenuItem value="ALL">Tous (ALPHA + BETA)</MenuItem>
              <MenuItem value="ALPHA">ALPHA</MenuItem>
              <MenuItem value="BETA">BETA</MenuItem>
              <MenuItem value="GAMMA">GAMMA</MenuItem>
              <MenuItem value="OMEGA">OMEGA</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>

      <TableContainer style={{ "flexGrow": "1", maxHeight: "75%", overflowY: "auto" }}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead className="bg2">
            <TableRow>
              <StyledTableCell align="center" style={{ "fontWeight": "bold" }}>
                Rang
              </StyledTableCell>
              <StyledTableCell align="center" style={{ "fontWeight": "bold" }}>
                Joueur
              </StyledTableCell>
              {role === "ADMIN" && (
                <StyledTableCell align="center" style={{ "fontWeight": "bold" }}>
                  Serveur
                </StyledTableCell>
              )}
              <StyledTableCell align="center" style={{ "fontWeight": "bold" }}>
                Date
              </StyledTableCell>
              <StyledTableCell align="center" style={{ "fontWeight": "bold" }}>
                Score
              </StyledTableCell>
              {role === "ADMIN" ? <StyledTableCell align="center" style={{ "fontWeight": "bold" }} /> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {players.map((player, idx) => (
              <StyledTableRow key={idx}>

                <StyledTableCell align="center">
                  {idx + 1}
                </StyledTableCell>

                <StyledTableCell align="center">
                  {player.user}
                </StyledTableCell>

                {role === "ADMIN" && (
                  <StyledTableCell align="center">
                    {player.server || "-"}
                  </StyledTableCell>
                )}

                <StyledTableCell align="center">
                  {printDate(player.date)}
                </StyledTableCell>

                <StyledTableCell align="center">
                  {player.score}
                </StyledTableCell>

                {role === "ADMIN" ? <StyledTableCell align="center">
                  <Button variant="contained" startIcon={<DeleteIcon />} onClick={() => deletePlayer(player.id, player.server)}>
                    Supprimer {player.server ? `(${player.server})` : ""}
                  </Button>
                </StyledTableCell> : null}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ColorButton variant="contained" sx={{ marginTop: '1em' }} onClick={goToHome}>
        Quitter
      </ColorButton>
    </Paper>
  );
};


const ColorButton = styled(Button)<ButtonProps>(() => ({
  color: "white",
  backgroundColor: purple[500],
  '&:hover': {
    backgroundColor: purple[700],
  },
  width: "fit-content",
  alignSelf: "end",
}));


export default Scoreboard;